// src/components/features/ExpenseFiltersToolbar/ExpenseFiltersToolbar.tsx
//
// Reusable toolbar with filters for expense-based pages.
//
// Desktop: [Add] [Refresh] [extraActions]  ...  [Search] [Filters button]
//          Active filter chips row below (when filters are active)
//
// Mobile:  [Search] [Filters button]
//          Active filter chips row below (when filters are active)
//
// onFiltersChange emit points (all synchronous, no useEffect-based emit):
//   1. Apply in drawer — drawer passes DrawerApplyValues + extra values
//      so we never read stale context
//   2. Chip removal — reads from context (safe, context just updated)
//   3. Search keyword change — inline, value known synchronously
//   4. Initial mount — single useEffect for first emit only

import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { Group, Stack, Badge, CloseButton, ScrollArea } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { EntityType } from "@/types/filters";
import { ENTITIES, OPERATION_RIGHTS } from "@/config";
import {
  getExpenseTypeOptionsByMode,
  type ExpenseTypeMode,
} from "@/config/expenseTypes";

import { getCarDisplayName } from "@/utils/carHelpers";

import { useEntityPageShortcuts } from "@/hooks/useKeyboardShortcuts";

import { useEntityFiltersContext } from "@/contexts/EntityFiltersContext";
import { useApp } from "@/contexts/AppContext";
import { useDashboardContext } from "@/contexts/DashboardContext";
import { useAppEnvironment } from "@/contexts/AppEnvironmentContext";
import { useExpenseTagsContext } from "@/contexts/ExpenseTagsContext";
import { useAppEssentialsContext } from "@/contexts/AppEssentialsContext";

import { AppButton } from "@/components/generic/AppButton";
import { AppToolbar } from "@/components/generic/AppToolbar";
import { SearchInput } from "@/components/generic/SearchInput";

import {
  ExpenseFiltersDrawer,
  CarFilterMode,
  ExtraFiltersHandle,
  DrawerApplyValues,
} from "./ExpenseFiltersDrawer";
import { StickyBox } from "@/components/generic";

// ============================================
// Types
// ============================================

export interface ExpenseFiltersToolbarProps {
  /** Entity type for permission checking */
  entityType: EntityType;

  /** Handler for add button click */
  onAdd?: () => void;

  /** Handler for refresh button click */
  onRefresh?: () => void;

  /** Whether add button is disabled */
  addDisabled?: boolean;

  /** Whether refresh button is disabled */
  refreshDisabled?: boolean;

  /** Whether data is loading */
  loading?: boolean;

  /** Custom label for add button */
  addLabel?: string;

  /**
   * Car filter mode:
   * - "none"   — no car filter shown
   * - "single" — single car selector (default), auto-hidden if only 1 car
   * - "multi"  — multi-car selector
   */
  carFilterMode?: CarFilterMode;

  /** Show date range filter (default: true) */
  showDateFilter?: boolean;

  /** Show tags filter (default: true) */
  showTagFilter?: boolean;

  /** Show expense type filter (default: false) */
  showExpenseTypeFilter?: boolean;

  /** Which expense types to show */
  expenseTypeMode?: ExpenseTypeMode;

  /** Show expense kind multi-select filter (default: false) */
  showExpenseKindFilter?: boolean;

  /** Show refresh button (default: true) */
  showRefresh?: boolean;

  /** Show search input (default: false) */
  showSearch?: boolean;

  /** Placeholder text for search input */
  searchPlaceholder?: string;

  /** Enable keyboard shortcuts (default: true) */
  enableShortcuts?: boolean;

  /** User has to select a car */
  carRequired?: boolean;

  /** Additional action buttons (rendered after Add/Refresh) */
  extraActions?: React.ReactNode;

  /** Additional filter components (rendered after built-in filters in drawer) */
  extraFilters?: React.ReactNode;

  /** Ref to page-specific extra filters handle */
  extraFiltersRef?: React.RefObject<ExtraFiltersHandle | null>;

  /** Additional filter chips */
  extraChips?: React.ReactNode;

  /**
   * Callback emitting a combined filter object built from all enabled sources.
   * Receives null when all filters are cleared / empty.
   */
  onFiltersChange?: (filters: Record<string, unknown> | null) => void;

  /** @deprecated Use carFilterMode="single" (default) or "none" instead */
  showCarFilter?: boolean;
}

// ============================================
// Entity to Permission Mapping
// ============================================

const entityToPermissionMap: Record<EntityType, string> = {
  refuels: ENTITIES.EXPENSES,
  expenses: ENTITIES.EXPENSES,
  checkpoints: ENTITIES.CHECKPOINTS,
  travels: ENTITIES.TRAVELS,
  reports: ENTITIES.REPORTS,
  "upcoming-services": ENTITIES.UPCOMING_SERVICES,
  glovebox: ENTITIES.GLOVEBOX,
  revenues: ENTITIES.REVENUE,
  "expense-schedules": ENTITIES.EXPENSE_SCHEDULES,
  "vehicle-financing": ENTITIES.VEHICLE_FINANCING,
  "tire-sets": ENTITIES.TIRE_SET,
  "vehicle-tasks": ENTITIES.VEHICLE_TASKS,
  "parking-sessions": ENTITIES.PARKING_SESSIONS,
  logbook: ENTITIES.EXPENSES,
  tags: ENTITIES.EXPENSE_TAGS,
  users: ENTITIES.USERS,
  invitations: ENTITIES.USER_INVITATIONS,
  cars: ENTITIES.CARS,
  "saved-places": ENTITIES.SAVED_PLACES,
};

// ============================================
// Helper: Build combined filter object
// ============================================

interface BuildFiltersParams {
  carFilterMode: CarFilterMode;
  showDateFilter: boolean;
  showTagFilter: boolean;
  showExpenseTypeFilter: boolean;
  showExpenseKindFilter: boolean;
  showSearch: boolean;
  builtInValues: {
    carId: string | null;
    multiCarIds: string[] | null;
    dateFrom: string | null;
    dateTo: string | null;
    tagIds: string[];
    expenseType: number | null;
    expenseKindIds: number[];
  };
  searchKeyword: string;
  extraValues?: Record<string, unknown>;
}

function buildCombinedFilters(
  params: BuildFiltersParams
): Record<string, unknown> | null {
  const result: Record<string, unknown> = {};
  let hasAny = false;

  if (params.carFilterMode === "single" && params.builtInValues.carId) {
    result.carId = [params.builtInValues.carId];
    hasAny = true;
  }

  if (
    params.carFilterMode === "multi" &&
    params.builtInValues.multiCarIds &&
    params.builtInValues.multiCarIds.length > 0
  ) {
    result.carId = params.builtInValues.multiCarIds;
    hasAny = true;
  }

  if (params.showDateFilter) {
    if (params.builtInValues.dateFrom) {
      result.dateFrom = params.builtInValues.dateFrom;
      hasAny = true;
    }
    if (params.builtInValues.dateTo) {
      result.dateTo = params.builtInValues.dateTo;
      hasAny = true;
    }
  }

  if (params.showTagFilter && params.builtInValues.tagIds.length > 0) {
    result.tagIds = params.builtInValues.tagIds;
    hasAny = true;
  }

  if (
    params.showExpenseTypeFilter &&
    params.builtInValues.expenseType != null
  ) {
    result.expenseType = params.builtInValues.expenseType;
    hasAny = true;
  }

  if (
    params.showExpenseKindFilter &&
    params.builtInValues.expenseKindIds.length > 0
  ) {
    result.expenseKindIds = params.builtInValues.expenseKindIds;
    hasAny = true;
  }

  if (params.showSearch && params.searchKeyword.trim()) {
    result.searchKeyword = params.searchKeyword.trim();
    hasAny = true;
  }

  if (params.extraValues) {
    for (const [key, value] of Object.entries(params.extraValues)) {
      if (
        value != null &&
        value !== "" &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        result[key] = value;
        hasAny = true;
      }
    }
  }

  return hasAny ? result : null;
}

// ============================================
// ActiveFilterChips
// ============================================

interface ActiveFilterChipsProps {
  carFilterMode: CarFilterMode;
  showDateFilter: boolean;
  showTagFilter: boolean;
  showExpenseTypeFilter: boolean;
  showExpenseKindFilter: boolean;
  expenseTypeMode: ExpenseTypeMode;
  multiCarIds: string[] | null;
  expenseKindIds: number[];
  extraChips?: React.ReactNode;
  onRemoveMultiCar?: (carId: string) => void;
  onClearMultiCars?: () => void;
  onRemoveExpenseKind?: (kindId: number) => void;
  onChipRemoved?: () => void;
}

const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  carFilterMode,
  showDateFilter,
  showTagFilter,
  showExpenseTypeFilter,
  showExpenseKindFilter,
  expenseTypeMode,
  multiCarIds,
  expenseKindIds,
  extraChips,
  onRemoveMultiCar,
  onRemoveExpenseKind,
  onChipRemoved,
}) => {
  const { t } = useTranslation(["common"]);
  const { getCarById } = useDashboardContext();
  const { getTagsByIds } = useExpenseTagsContext();
  const { getExpenseKindById } = useAppEssentialsContext();
  const {
    filters,
    setCarId,
    setDateRange,
    setTagIds,
    setExpenseType,
  } = useEntityFiltersContext();

  // Single-car chip
  const carChipLabel = useMemo(() => {
    if (carFilterMode !== "single" || !filters.carId) return null;
    const car = getCarById(filters.carId);
    if (!car) return null;
    return getCarDisplayName(car) || car.label || null;
  }, [carFilterMode, filters.carId, getCarById]);

  const handleRemoveCar = useCallback(() => {
    setCarId(null);
    onChipRemoved?.();
  }, [setCarId, onChipRemoved]);

  // Multi-car chips
  const multiCarChips = useMemo(() => {
    if (
      carFilterMode !== "multi" ||
      !multiCarIds ||
      multiCarIds.length === 0
    ) {
      return [];
    }
    return multiCarIds
      .map((id) => {
        const car = getCarById(id);
        if (!car) return null;
        return { id, label: getCarDisplayName(car) || car.label || "Vehicle" };
      })
      .filter((chip): chip is { id: string; label: string } => chip !== null);
  }, [carFilterMode, multiCarIds, getCarById]);

  const handleRemoveMultiCar = useCallback(
    (carId: string) => onRemoveMultiCar?.(carId),
    [onRemoveMultiCar]
  );

  // Date chip
  const dateChipLabel = useMemo(() => {
    if (!showDateFilter) return null;
    if (!filters.dateFrom && !filters.dateTo) return null;
    const from = filters.dateFrom
      ? dayjs(filters.dateFrom).format("MMM D, YYYY")
      : "…";
    const to = filters.dateTo
      ? dayjs(filters.dateTo).format("MMM D, YYYY")
      : "…";
    return `${from} – ${to}`;
  }, [showDateFilter, filters.dateFrom, filters.dateTo]);

  const handleRemoveDate = useCallback(() => {
    setDateRange(null, null);
    onChipRemoved?.();
  }, [setDateRange, onChipRemoved]);

  // Tag chips
  const tagChips = useMemo(() => {
    if (!showTagFilter || !filters.tagIds || filters.tagIds.length === 0) {
      return [];
    }
    const tags = getTagsByIds(filters.tagIds);
    return tags.map((tag) => ({
      id: tag.id || "",
      label: tag.tagName || "",
      color: tag.tagColor || "gray",
    }));
  }, [showTagFilter, filters.tagIds, getTagsByIds]);

  const handleRemoveTag = useCallback(
    (tagId: string) => {
      const newTagIds = (filters.tagIds || []).filter((id) => id !== tagId);
      setTagIds(newTagIds);
      onChipRemoved?.();
    },
    [filters.tagIds, setTagIds, onChipRemoved]
  );

  // Expense type chip
  const expenseTypeChipLabel = useMemo(() => {
    if (!showExpenseTypeFilter || !filters.expenseType) return null;
    const options = getExpenseTypeOptionsByMode(expenseTypeMode);
    const option = options.find((opt) => opt.value === filters.expenseType);
    if (!option) return null;
    return t(option.labelKey);
  }, [showExpenseTypeFilter, filters.expenseType, expenseTypeMode, t]);

  const handleRemoveExpenseType = useCallback(() => {
    setExpenseType(null);
    onChipRemoved?.();
  }, [setExpenseType, onChipRemoved]);

  // Expense kind chips
  const expenseKindChips = useMemo(() => {
    if (!showExpenseKindFilter || expenseKindIds.length === 0) return [];
    return expenseKindIds
      .map((id) => {
        const kind = getExpenseKindById(id);
        if (!kind) return null;
        return { id, label: kind.name || "" };
      })
      .filter((chip): chip is { id: number; label: string } => chip !== null);
  }, [showExpenseKindFilter, expenseKindIds, getExpenseKindById]);

  const handleRemoveExpenseKind = useCallback(
    (kindId: number) => {
      onRemoveExpenseKind?.(kindId);
    },
    [onRemoveExpenseKind]
  );

  const hasBuiltInChips =
    carChipLabel ||
    multiCarChips.length > 0 ||
    dateChipLabel ||
    tagChips.length > 0 ||
    expenseTypeChipLabel ||
    expenseKindChips.length > 0;

  if (!hasBuiltInChips && !extraChips) return null;

  return (
    <ScrollArea type="never" scrollbarSize={0}>
      <Group gap="xs" wrap="nowrap">
        {/* Single car chip */}
        {carChipLabel && (
          <Badge
            variant="light"
            color="blue"
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={handleRemoveCar}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {carChipLabel}
          </Badge>
        )}

        {/* Multi car chips */}
        {multiCarChips.map((chip) => (
          <Badge
            key={chip.id}
            variant="light"
            color="blue"
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={() => handleRemoveMultiCar(chip.id)}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {chip.label}
          </Badge>
        ))}

        {/* Expense type chip */}
        {expenseTypeChipLabel && (
          <Badge
            variant="light"
            color="violet"
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={handleRemoveExpenseType}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {expenseTypeChipLabel}
          </Badge>
        )}

        {/* Expense kind chips */}
        {expenseKindChips.map((chip) => (
          <Badge
            key={chip.id}
            variant="light"
            color="orange"
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={() => handleRemoveExpenseKind(chip.id)}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {chip.label}
          </Badge>
        ))}

        {/* Tag chips */}
        {tagChips.map((tag) => (
          <Badge
            key={tag.id}
            variant="light"
            color={tag.color}
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={() => handleRemoveTag(tag.id)}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {tag.label}
          </Badge>
        ))}

        {/* Date chip */}
        {dateChipLabel && (
          <Badge
            variant="light"
            color="gray"
            size="lg"
            radius="sm"
            pr={3}
            style={{ flexShrink: 0 }}
            rightSection={
              <CloseButton
                size="xs"
                variant="transparent"
                onClick={handleRemoveDate}
                aria-label={t("common:buttons.clear")}
              />
            }
          >
            {dateChipLabel}
          </Badge>
        )}

        {extraChips}
      </Group>
    </ScrollArea>
  );
};

// ============================================
// ExpenseFiltersToolbar Component
// ============================================

export const ExpenseFiltersToolbar: React.FC<ExpenseFiltersToolbarProps> = ({
  entityType,
  onAdd,
  onRefresh,
  addDisabled = false,
  refreshDisabled = false,
  loading = false,
  addLabel,
  carFilterMode: carFilterModeProp,
  showCarFilter,
  showDateFilter = true,
  showTagFilter = true,
  showExpenseTypeFilter = false,
  expenseTypeMode = "all",
  showExpenseKindFilter = false,
  showRefresh = true,
  showSearch = false,
  searchPlaceholder,
  enableShortcuts = true,
  carRequired,
  extraActions,
  extraFilters,
  extraFiltersRef,
  extraChips,
  onFiltersChange,
}) => {
  const { t } = useTranslation(["common"]);
  const { hasRightTo } = useApp();
  const { isMobile } = useAppEnvironment();
  const { totalCarsCount, isLoaded: carsLoaded } = useDashboardContext();

  const [filtersOpened, { open: openFilters, close: closeFilters }] =
    useDisclosure(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const searchKeywordRef = useRef(searchKeyword);
  searchKeywordRef.current = searchKeyword;

  // Multi-car selection — managed locally in the toolbar, not in EntityFiltersContext.
  const [multiCarIds, setMultiCarIds] = useState<string[] | null>(null);
  const multiCarIdsRef = useRef<string[] | null>(null);

  // Expense kind selection — managed locally in the toolbar (not in EntityFiltersContext,
  // which only holds carId, dates, tags, expenseType).
  const [expenseKindIds, setExpenseKindIds] = useState<number[]>([]);
  const expenseKindIdsRef = useRef<number[]>([]);

  const { filters } = useEntityFiltersContext();
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const handleMultiCarChange = useCallback((carIds: string[] | null) => {
    setMultiCarIds(carIds);
    multiCarIdsRef.current = carIds;
  }, []);

  const handleExpenseKindChange = useCallback((kindIds: number[]) => {
    setExpenseKindIds(kindIds);
    expenseKindIdsRef.current = kindIds;
  }, []);

  const resolvedCarFilterMode = useMemo((): CarFilterMode => {
    if (carFilterModeProp !== undefined) return carFilterModeProp;
    if (showCarFilter === false) return "none";
    return "single";
  }, [carFilterModeProp, showCarFilter]);

  const effectiveCarFilterMode: CarFilterMode =
    resolvedCarFilterMode !== "none" && totalCarsCount <= 1
      ? "none"
      : resolvedCarFilterMode;

  const permissionEntity = entityToPermissionMap[entityType];
  const canCreate = hasRightTo(permissionEntity, OPERATION_RIGHTS.CREATE);

  // ============================================
  // Drawer has filters?
  // ============================================

  const hasDrawerFilters = useMemo(() => {
    if (effectiveCarFilterMode !== "none") return true;
    if (showDateFilter) return true;
    if (showTagFilter) return true;
    if (showExpenseTypeFilter) return true;
    if (showExpenseKindFilter) return true;
    if (extraFilters) return true;
    return false;
  }, [
    effectiveCarFilterMode,
    showDateFilter,
    showTagFilter,
    showExpenseTypeFilter,
    showExpenseKindFilter,
    extraFilters,
  ]);

  // ============================================
  // Active Filter Count
  // ============================================

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (effectiveCarFilterMode === "single" && filters.carId) count++;
    if (
      effectiveCarFilterMode === "multi" &&
      multiCarIds &&
      multiCarIds.length > 0
    ) {
      count++;
    }
    if (filters.tagIds && filters.tagIds.length > 0) count++;
    if (filters.dateFrom || filters.dateTo) count++;
    if (showExpenseTypeFilter && filters.expenseType) count++;
    if (showExpenseKindFilter && expenseKindIds.length > 0) count++;
    return count;
  }, [
    effectiveCarFilterMode,
    filters.carId,
    multiCarIds,
    filters.tagIds,
    filters.dateFrom,
    filters.dateTo,
    filters.expenseType,
    showExpenseTypeFilter,
    showExpenseKindFilter,
    expenseKindIds,
  ]);

  // ============================================
  // Helper: build builtInValues snapshot
  // ============================================

  const buildBuiltInValues = useCallback(
    () => ({
      carId: filtersRef.current.carId ?? null,
      multiCarIds: multiCarIdsRef.current,
      dateFrom: filtersRef.current.dateFrom ?? null,
      dateTo: filtersRef.current.dateTo ?? null,
      tagIds: filtersRef.current.tagIds ?? [],
      expenseType: filtersRef.current.expenseType ?? null,
      expenseKindIds: expenseKindIdsRef.current,
    }),
    []
  );

  // ============================================
  // Build & Emit — from context (for non-Apply paths)
  // ============================================

  const buildAndEmitFromContext = useCallback(
    (extraValues?: Record<string, unknown>) => {
      if (!onFiltersChange) return;

      const resolvedExtraValues =
        extraValues ?? extraFiltersRef?.current?.getValues?.() ?? undefined;

      const combined = buildCombinedFilters({
        carFilterMode: effectiveCarFilterMode,
        showDateFilter,
        showTagFilter,
        showExpenseTypeFilter,
        showExpenseKindFilter,
        showSearch,
        builtInValues: buildBuiltInValues(),
        searchKeyword: searchKeywordRef.current,
        extraValues: resolvedExtraValues,
      });

      onFiltersChange(combined);
    },
    [
      onFiltersChange,
      effectiveCarFilterMode,
      showDateFilter,
      showTagFilter,
      showExpenseTypeFilter,
      showExpenseKindFilter,
      showSearch,
      extraFiltersRef,
      buildBuiltInValues,
    ]
  );

  // ============================================
  // Initial emit on mount
  // ============================================

  const hasEmittedInitialRef = useRef(false);

  useEffect(() => {
    if (!onFiltersChange || hasEmittedInitialRef.current) {
      return;
    }

    if (!carsLoaded) {
      // we have to wait till all the cars are loaded before doing any filtering
      return;
    }

    hasEmittedInitialRef.current = true;
    buildAndEmitFromContext();
  }, [onFiltersChange, buildAndEmitFromContext, carsLoaded]);

  // ============================================
  // Detect extra filter value changes from outside the toolbar
  // ============================================

  const prevExtraValuesJsonRef = useRef<string | null>(null);

  useEffect(() => {
    if (!onFiltersChange || !extraFiltersRef?.current?.getValues) return;
    if (!hasEmittedInitialRef.current) return;

    const currentValues = extraFiltersRef.current.getValues();
    const currentJson = JSON.stringify(currentValues ?? {});

    if (prevExtraValuesJsonRef.current === null) {
      prevExtraValuesJsonRef.current = currentJson;
      return;
    }

    if (currentJson !== prevExtraValuesJsonRef.current) {
      prevExtraValuesJsonRef.current = currentJson;
      setTimeout(() => {
        buildAndEmitFromContext();
      }, 0);
    }
  }); // Intentionally no deps — runs every render to catch external changes

  // ============================================
  // Wrapped extraFiltersRef
  // ============================================

  const pendingApplyExtraValuesRef = useRef<
    Record<string, unknown> | undefined
  >(undefined);

  const wrappedExtraFiltersRef = useRef<ExtraFiltersHandle>({
    onOpen: () => {
      extraFiltersRef?.current?.onOpen?.();
    },
    onApply: () => {
      const result = extraFiltersRef?.current?.onApply();
      if (result && typeof result === "object") {
        pendingApplyExtraValuesRef.current = result;
      }
      return result;
    },
    onClear: () => {
      extraFiltersRef?.current?.onClear();
    },
    hasDraftValues: () =>
      extraFiltersRef?.current?.hasDraftValues?.() ?? false,
    getValues: () => extraFiltersRef?.current?.getValues?.() ?? {},
  });

  wrappedExtraFiltersRef.current.onOpen = () => {
    extraFiltersRef?.current?.onOpen?.();
  };
  wrappedExtraFiltersRef.current.onApply = () => {
    const result = extraFiltersRef?.current?.onApply();
    if (result && typeof result === "object") {
      pendingApplyExtraValuesRef.current = result;
    }
    return result;
  };
  wrappedExtraFiltersRef.current.onClear = () => {
    extraFiltersRef?.current?.onClear();
  };
  wrappedExtraFiltersRef.current.hasDraftValues = () => {
    return extraFiltersRef?.current?.hasDraftValues?.() ?? false;
  };
  wrappedExtraFiltersRef.current.getValues = () => {
    return extraFiltersRef?.current?.getValues?.() ?? {};
  };

  // ============================================
  // Drawer Apply Handler
  // ============================================

  const handleDrawerApply = useCallback(
    (drawerValues: DrawerApplyValues) => {
      if (!onFiltersChange) {
        closeFilters();
        return;
      }

      const builtInValues = buildBuiltInValues();

      if ("carId" in drawerValues) {
        builtInValues.carId = drawerValues.carId ?? null;
      }
      if ("multiCarIds" in drawerValues) {
        builtInValues.multiCarIds = drawerValues.multiCarIds ?? null;
        setMultiCarIds(drawerValues.multiCarIds ?? null);
        multiCarIdsRef.current = drawerValues.multiCarIds ?? null;
      }
      if ("dateFrom" in drawerValues) {
        builtInValues.dateFrom = drawerValues.dateFrom ?? null;
      }
      if ("dateTo" in drawerValues) {
        builtInValues.dateTo = drawerValues.dateTo ?? null;
      }
      if ("tagIds" in drawerValues) {
        builtInValues.tagIds = drawerValues.tagIds ?? [];
      }
      if ("expenseType" in drawerValues) {
        builtInValues.expenseType = drawerValues.expenseType ?? null;
      }
      if ("expenseKindIds" in drawerValues) {
        const kinds = drawerValues.expenseKindIds ?? [];
        builtInValues.expenseKindIds = kinds;
        setExpenseKindIds(kinds);
        expenseKindIdsRef.current = kinds;
      }

      const extraValues = pendingApplyExtraValuesRef.current;
      pendingApplyExtraValuesRef.current = undefined;

      const combined = buildCombinedFilters({
        carFilterMode: effectiveCarFilterMode,
        showDateFilter,
        showTagFilter,
        showExpenseTypeFilter,
        showExpenseKindFilter,
        showSearch,
        builtInValues,
        searchKeyword: searchKeywordRef.current,
        extraValues,
      });

      onFiltersChange(combined);
      closeFilters();

      prevExtraValuesJsonRef.current = JSON.stringify(extraValues ?? {});
    },
    [
      onFiltersChange,
      effectiveCarFilterMode,
      showDateFilter,
      showTagFilter,
      showExpenseTypeFilter,
      showExpenseKindFilter,
      showSearch,
      closeFilters,
      buildBuiltInValues,
    ]
  );

  const handleDrawerClose = useCallback(() => {
    closeFilters();
  }, [closeFilters]);

  // ============================================
  // Multi-car chip removal
  // ============================================

  const handleRemoveMultiCar = useCallback(
    (carId: string) => {
      const newIds = (multiCarIds || []).filter((id) => id !== carId);
      const result = newIds.length > 0 ? newIds : null;
      setMultiCarIds(result);
      multiCarIdsRef.current = result;

      if (!onFiltersChange) return;
      setTimeout(() => {
        buildAndEmitFromContext();
      }, 0);
    },
    [multiCarIds, onFiltersChange, buildAndEmitFromContext]
  );

  const handleClearMultiCars = useCallback(() => {
    setMultiCarIds(null);
    multiCarIdsRef.current = null;

    if (!onFiltersChange) return;
    setTimeout(() => {
      buildAndEmitFromContext();
    }, 0);
  }, [onFiltersChange, buildAndEmitFromContext]);

  // ============================================
  // Expense kind chip removal
  // ============================================

  const handleRemoveExpenseKind = useCallback(
    (kindId: number) => {
      const newIds = expenseKindIds.filter((id) => id !== kindId);
      setExpenseKindIds(newIds);
      expenseKindIdsRef.current = newIds;

      if (!onFiltersChange) return;
      setTimeout(() => {
        buildAndEmitFromContext();
      }, 0);
    },
    [expenseKindIds, onFiltersChange, buildAndEmitFromContext]
  );

  // ============================================
  // Chip Removal (non-multi-car) — emit after context updates
  // ============================================

  const handleChipRemoved = useCallback(() => {
    if (!onFiltersChange) return;
    setTimeout(() => {
      buildAndEmitFromContext();
    }, 0);
  }, [onFiltersChange, buildAndEmitFromContext]);

  // ============================================
  // Search — emit immediately
  // ============================================

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchKeyword(value);
      searchKeywordRef.current = value;

      if (!onFiltersChange) return;

      const extraValues =
        extraFiltersRef?.current?.getValues?.() ?? undefined;

      const combined = buildCombinedFilters({
        carFilterMode: effectiveCarFilterMode,
        showDateFilter,
        showTagFilter,
        showExpenseTypeFilter,
        showExpenseKindFilter,
        showSearch,
        builtInValues: buildBuiltInValues(),
        searchKeyword: value,
        extraValues,
      });

      onFiltersChange(combined);
    },
    [
      onFiltersChange,
      effectiveCarFilterMode,
      showDateFilter,
      showTagFilter,
      showExpenseTypeFilter,
      showExpenseKindFilter,
      showSearch,
      extraFiltersRef,
      buildBuiltInValues,
    ]
  );

  // ============================================
  // Keyboard Shortcuts
  // ============================================

  useEntityPageShortcuts({
    onAdd: canCreate && !addDisabled && onAdd ? onAdd : undefined,
    onRefresh: !refreshDisabled && onRefresh ? onRefresh : undefined,
    canAdd: canCreate && !addDisabled && !loading,
    canRefresh: !refreshDisabled && !loading,
    enabled: enableShortcuts,
  });

  // ============================================
  // UI Elements
  // ============================================

  const searchInput = useMemo(() => {
    if (!showSearch) return null;
    return (
      <SearchInput
        key="search"
        placeholder={
          searchPlaceholder ||
          t("common:filters.search", { defaultValue: "Search..." })
        }
        value={searchKeyword}
        onChange={handleSearchChange}
        style={{ minWidth: 200, flex: isMobile ? 1 : undefined }}
      />
    );
  }, [
    showSearch,
    searchPlaceholder,
    searchKeyword,
    handleSearchChange,
    isMobile,
    t,
  ]);

  const filtersButton = useMemo(() => {
    if (!hasDrawerFilters) return null;
    return (
      <AppButton
        key="filters"
        variant={activeFilterCount > 0 ? "primary" : "secondary"}
        iconName="Filter"
        onClick={openFilters}
      >
        {t("common:buttons.filters", { defaultValue: "Filters" })}
        {activeFilterCount > 0 && ` (${activeFilterCount})`}
      </AppButton>
    );
  }, [hasDrawerFilters, activeFilterCount, openFilters, t]);

  const actionItems = useMemo(() => {
    const items: React.ReactNode[] = [];
    if (onAdd) {
      items.push(
        <AppButton
          key="add"
          iconName="Plus"
          onClick={onAdd}
          disabled={addDisabled || loading || !canCreate}
        >
          {addLabel || t("common:buttons.add")}
        </AppButton>
      );
    }
    if (onRefresh && showRefresh) {
      items.push(
        <AppButton
          key="refresh"
          variant="secondary"
          iconName="Refresh"
          onClick={onRefresh}
          disabled={refreshDisabled || loading}
          loading={loading}
        />
      );
    }
    if (extraActions) {
      items.push(extraActions);
    }
    return items.filter(Boolean);
  }, [
    onAdd,
    onRefresh,
    addDisabled,
    refreshDisabled,
    loading,
    canCreate,
    addLabel,
    extraActions,
    showRefresh,
    t,
  ]);

  const rightItems = useMemo(() => {
    const items: React.ReactNode[] = [];
    if (searchInput) items.push(searchInput);
    if (filtersButton) items.push(filtersButton);
    return items;
  }, [searchInput, filtersButton]);

  const filterChips = useMemo(
    () => (
      <ActiveFilterChips
        carFilterMode={effectiveCarFilterMode}
        showDateFilter={showDateFilter}
        showTagFilter={showTagFilter}
        showExpenseTypeFilter={showExpenseTypeFilter}
        showExpenseKindFilter={showExpenseKindFilter}
        expenseTypeMode={expenseTypeMode}
        multiCarIds={multiCarIds}
        expenseKindIds={expenseKindIds}
        extraChips={extraChips}
        onRemoveMultiCar={handleRemoveMultiCar}
        onClearMultiCars={handleClearMultiCars}
        onRemoveExpenseKind={handleRemoveExpenseKind}
        onChipRemoved={handleChipRemoved}
      />
    ),
    [
      effectiveCarFilterMode,
      showDateFilter,
      showTagFilter,
      showExpenseTypeFilter,
      showExpenseKindFilter,
      expenseTypeMode,
      multiCarIds,
      expenseKindIds,
      extraChips,
      handleRemoveMultiCar,
      handleClearMultiCars,
      handleRemoveExpenseKind,
      handleChipRemoved,
    ]
  );

  // ============================================
  // Filters Drawer
  // ============================================

  const filtersDrawer = useMemo(() => {
    if (!hasDrawerFilters) return null;
    return (
      <ExpenseFiltersDrawer
        opened={filtersOpened}
        onClose={handleDrawerClose}
        onApply={onFiltersChange ? handleDrawerApply : undefined}
        carFilterMode={effectiveCarFilterMode}
        showDateFilter={showDateFilter}
        showTagFilter={showTagFilter}
        showExpenseTypeFilter={showExpenseTypeFilter}
        expenseTypeMode={expenseTypeMode}
        showExpenseKindFilter={showExpenseKindFilter}
        expenseKindIds={expenseKindIds}
        onExpenseKindChange={handleExpenseKindChange}
        carRequired={carRequired}
        extraFilters={extraFilters}
        extraFiltersRef={wrappedExtraFiltersRef}
        multiCarIds={multiCarIds}
        onMultiCarChange={handleMultiCarChange}
      />
    );
  }, [
    hasDrawerFilters,
    filtersOpened,
    handleDrawerClose,
    handleDrawerApply,
    onFiltersChange,
    effectiveCarFilterMode,
    showDateFilter,
    showTagFilter,
    showExpenseTypeFilter,
    expenseTypeMode,
    showExpenseKindFilter,
    expenseKindIds,
    handleExpenseKindChange,
    carRequired,
    extraFilters,
    multiCarIds,
    handleMultiCarChange,
  ]);

  // ============================================
  // Layout
  // ============================================

  if (isMobile) {
    return (
      <StickyBox>
        <Stack gap="xs">
          <Group gap="xs" wrap="nowrap">
            {searchInput}
            {filtersButton}
          </Group>
          {filterChips}
        </Stack>
        {filtersDrawer}
      </StickyBox>
    );
  }

  return (
    <>
      <AppToolbar
        preset="page-header"
        sections={[
          { id: "actions", items: actionItems, align: "left", gap: "sm" },
          { id: "filters", items: rightItems, align: "right", gap: "sm" },
        ]}
        filterChips={filterChips}
      />
      {filtersDrawer}
    </>
  );
};